import React, { useState } from 'react';
import { Terminal, Send } from 'lucide-react';

const Contact = () => {
  const [terminalInput, setTerminalInput] = useState('');
  const [terminalHistory, setTerminalHistory] = useState([
    '>> COMMUNICATION CHANNEL INITIALIZED',
    '>> WAITING FOR INPUT...'
  ]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!terminalInput.trim()) return;

    setIsProcessing(true);
    const userMessage = terminalInput;
    
    // Add user message to history
    setTerminalHistory(prev => [
      ...prev,
      `> ${userMessage}`,
      '>> PROCESSING TRANSMISSION...',
    ]);

    // Simulate processing
    setTimeout(() => {
      setTerminalHistory(prev => [
        ...prev,
        '>> TRANSMISSION STORED LOCALLY',
        '>> STATUS: FRONTEND SIMULATION ONLY',
        '>> (Backend integration pending)',
        '>> READY FOR NEXT TRANSMISSION'
      ]);
      setTerminalInput('');
      setIsProcessing(false);
    }, 1500);
  };

  return (
    <section id="contact" style={{
      minHeight: '100vh',
      padding: '160px 7.6923% 100px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        {/* Section header */}
        <div style={{ marginBottom: '80px', textAlign: 'center' }}>
          <div style={{
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#00FFD1',
            marginBottom: '16px',
            letterSpacing: '2px'
          }}>
            {'>> SECURE CHANNEL'}
          </div>
          <h2 style={{
            fontSize: '48px',
            fontWeight: 600,
            lineHeight: 1.1,
            color: '#FFFFFF',
            marginBottom: '24px'
          }}>
            Open Communication
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontFamily: 'monospace'
          }}>
            Establish connection via terminal interface
          </p>
        </div>

        {/* Terminal interface */}
        <div style={{
          background: 'rgba(0, 0, 0, 0.8)',
          border: '2px solid #00FFD1',
          boxShadow: '0 0 40px rgba(0, 255, 209, 0.2)',
          padding: '24px',
          fontFamily: 'monospace',
          position: 'relative'
        }}>
          {/* Terminal header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '16px',
            borderBottom: '1px solid rgba(0, 255, 209, 0.3)',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Terminal size={20} style={{ color: '#00FFD1' }} />
              <span style={{ color: '#00FFD1', fontSize: '14px' }}>
                GOLDHAT.TERMINAL v1.0
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', background: '#FF0055', borderRadius: '50%' }} />
              <div style={{ width: '12px', height: '12px', background: '#FF9500', borderRadius: '50%' }} />
              <div style={{ width: '12px', height: '12px', background: '#00FFD1', borderRadius: '50%' }} />
            </div>
          </div>

          {/* Terminal output */}
          <div style={{
            minHeight: '300px',
            maxHeight: '400px',
            overflowY: 'auto',
            marginBottom: '24px',
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.4)'
          }}>
            {terminalHistory.map((line, index) => (
              <div
                key={index}
                style={{
                  fontSize: '14px',
                  lineHeight: 1.8,
                  color: line.startsWith('>>') ? '#00FFD1' : '#FFFFFF',
                  marginBottom: '8px',
                  animation: index === terminalHistory.length - 1 ? 'fadeIn 0.3s ease-out' : 'none'
                }}
              >
                {line}
              </div>
            ))}
            {isProcessing && (
              <div style={{
                fontSize: '14px',
                color: '#00FFD1',
                animation: 'blink 1s infinite'
              }}>
                _
              </div>
            )}
          </div>

          {/* Terminal input */}
          <form onSubmit={handleSubmit} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ color: '#00FFD1', fontSize: '14px' }}>{'>'}</span>
            <input
              type="text"
              value={terminalInput}
              onChange={(e) => setTerminalInput(e.target.value)}
              placeholder="Type your message and press Enter..."
              disabled={isProcessing}
              style={{
                flex: 1,
                background: 'rgba(0, 0, 0, 0.6)',
                border: '1px solid rgba(0, 255, 209, 0.3)',
                color: '#FFFFFF',
                padding: '12px 16px',
                fontSize: '14px',
                fontFamily: 'monospace',
                outline: 'none',
                transition: 'border 0.3s ease'
              }}
              onFocus={(e) => {
                e.currentTarget.style.border = '1px solid #00FFD1';
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = '1px solid rgba(0, 255, 209, 0.3)';
              }}
            />
            <button
              type="submit"
              disabled={isProcessing || !terminalInput.trim()}
              style={{
                background: '#00FFD1',
                color: '#000000',
                border: 'none',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                opacity: isProcessing || !terminalInput.trim() ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!isProcessing && terminalInput.trim()) {
                  e.currentTarget.style.background = 'rgba(0, 255, 209, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#00FFD1';
              }}
            >
              <Send size={16} />
              SEND
            </button>
          </form>
        </div>

        {/* Alternative contact info */}
        <div style={{
          marginTop: '60px',
          padding: '40px',
          background: 'rgba(0, 255, 209, 0.05)',
          border: '1px solid rgba(0, 255, 209, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: 'monospace',
            lineHeight: 1.8
          }}>
            <div style={{ marginBottom: '16px', color: '#00FFD1' }}>
              {'>> ALTERNATIVE CONTACT VECTORS'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap' }}>
              <a
                href="https://github.com/lalithsagarJ"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#00FFD1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                }}
              >
                github.com/lalithsagarJ
              </a>
              <a
                href="https://www.linkedin.com/in/lalithsagarj/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#00FFD1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                }}
              >
                linkedin.com/in/lalithsagarj
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default Contact;